import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/NavBar';
import { getChallengesByClub, getClubById, getClubLeaderboard } from '../services/api';
import { Trophy, Calendar, Users, Target, ArrowLeft, Plus, Repeat, Zap, History, X, Medal } from 'lucide-react';
import { DotLoader } from 'react-spinners';
import { useAuth } from '../context/AuthContext';
import defaultAvatar from '../assets/default-avatar.jpg';

function ClubChallenges() {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ponctuel'); // ✨ NOUVEAU
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    fetchData();
  }, [clubId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [challengesData, clubData, leaderboardData] = await Promise.all([
        getChallengesByClub(clubId),
        getClubById(clubId),
        getClubLeaderboard(clubId)
      ]);
      setChallenges(challengesData);
      setClub(clubData);
      setLeaderboard(leaderboardData);
      console.log('📊 Leaderboard chargé:', leaderboardData);
      setLoading(false);
    } catch (error) {
      console.error('Erreur:', error);
      setLoading(false);
    }
  };

  const isCreator = () => {
    if (!user || !club) return false;
    const createurId = typeof club.createur === 'object' ? club.createur._id : club.createur;
    return createurId === user._id;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getChallengeStatus = (challenge) => {
    const now = new Date();
    const start = new Date(challenge.dateDebut);
    const end = new Date(challenge.dateFin);

    if (now < start) return { text: 'À venir', color: 'bg-blue-900/20 border border-blue-500 text-blue-400' };
    if (now > end) return { text: 'Terminé', color: 'bg-slate-800 border border-slate-700 text-gray-400' };
    return { text: 'En cours', color: 'bg-green-900/20 border border-green-500 text-green-400' };
  };

  const getTypeLabel = (type) => {
    const types = {
      'distance_totale': 'Distance totale',
      'nombre_courses': 'Nombre de courses',
      'vitesse_moyenne': 'Vitesse moyenne',
      'denivele_total': 'Dénivelé total'
    };
    return types[type] || type;
  };

  const getMedalIcon = (position) => {
    if (position === 0) return '🥇';
    if (position === 1) return '🥈';
    if (position === 2) return '🥉';
    return null;
  };

  // ✨ FILTRER LES CHALLENGES PAR TYPE
  const recurringChallenges = challenges.filter(c => c.isTemplate && c.isRecurring);
  const normalChallenges = challenges.filter(c => !c.isTemplate && !c.parentChallenge);

  // Fonction pour trouver le challenge de la semaine actuelle ou à venir pour un template
  const getCurrentWeekChallengeForTemplate = (templateId) => {
    const now = new Date();

    // Chercher d'abord le challenge actif
    const currentChallenge = challenges.find(c =>
      c.parentChallenge === templateId &&
      !c.isTemplate &&
      new Date(c.dateDebut) <= now &&
      new Date(c.dateFin) >= now
    );

    if (currentChallenge) return currentChallenge;

    // Si aucun challenge actif, chercher le prochain à venir
    return challenges.find(c =>
      c.parentChallenge === templateId &&
      !c.isTemplate &&
      new Date(c.dateDebut) > now
    );
  };

  // ✨ FILTRER LES CHALLENGES TERMINÉS ET ACTIFS
  const activeChallenges = normalChallenges.filter(challenge => {
    const now = new Date();
    const end = new Date(challenge.dateFin);
    return now <= end; // En cours ou à venir
  });

  const completedChallenges = normalChallenges.filter(challenge => {
    const now = new Date();
    const end = new Date(challenge.dateFin);
    return now > end; // Terminés
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <DotLoader color="#6366f1" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Header */}
        <button
          onClick={() => navigate(`/club/${clubId}`)}
          className="flex items-center text-indigo-500 hover:text-indigo-400 font-semibold mb-4 sm:mb-6 transition text-sm sm:text-base"
        >
          <ArrowLeft size={18} className="mr-2 sm:w-5 sm:h-5" />
          Retour au club
        </button>

        <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="bg-yellow-900/30 border border-yellow-500 p-2 sm:p-3 rounded-xl">
                <Trophy size={24} className="text-yellow-500 sm:w-8 sm:h-8" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white">Challenges</h1>
                <p className="text-sm sm:text-base text-gray-400">{club?.nom}</p>
              </div>
            </div>

            {isCreator() && (
              <button
                onClick={() => navigate('/create-challenge', { state: { clubId } })}
                className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-lg font-semibold transition shadow-lg shadow-indigo-500/20 text-sm sm:text-base w-full sm:w-auto"
              >
                <Plus size={18} className="sm:w-5 sm:h-5" />
                Créer un challenge
              </button>
            )}
          </div>
        </div>

        {/* ✨ ONGLETS */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4 sm:mb-6 bg-slate-900 border border-slate-800 rounded-xl p-2">
          <button
            onClick={() => setActiveTab('ponctuel')}
            className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold transition text-sm sm:text-base ${
              activeTab === 'ponctuel'
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20'
                : 'text-gray-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Zap size={16} className="sm:w-5 sm:h-5" />
            <span className="hidden xs:inline">Ponctuels</span>
            <span className="xs:hidden">Ponct.</span>
            <span className="hidden sm:inline">({activeChallenges.length})</span>
            <span className="sm:hidden text-xs">({activeChallenges.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('recurrent')}
            className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold transition text-sm sm:text-base ${
              activeTab === 'recurrent'
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20'
                : 'text-gray-400 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Repeat size={16} className="sm:w-5 sm:h-5" />
            <span className="hidden xs:inline">Hebdomadaires</span>
            <span className="xs:hidden">Hebdo.</span>
            <span className="hidden sm:inline">({recurringChallenges.length})</span>
            <span className="sm:hidden text-xs">({recurringChallenges.length})</span>
          </button>

          <button
            onClick={() => setShowHistoryModal(true)}
            className="flex-1 flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold transition text-sm sm:text-base text-gray-400 hover:text-white hover:bg-slate-800"
          >
            <History size={16} className="sm:w-5 sm:h-5" />
            <span className="hidden xs:inline">Historique</span>
            <span className="xs:hidden">Hist.</span>
            <span className="hidden sm:inline">({completedChallenges.length})</span>
            <span className="sm:hidden text-xs">({completedChallenges.length})</span>
          </button>
        </div>

        {/* ✨ CONTENU DES ONGLETS */}
        {activeTab === 'ponctuel' && (
          <>
            {activeChallenges.length === 0 ? (
              <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-8 sm:p-12 text-center">
                <Trophy size={40} className="mx-auto text-gray-600 mb-4 sm:w-12 sm:h-12" />
                <p className="text-gray-400 text-base sm:text-lg">Aucun challenge ponctuel actif pour le moment</p>
                {isCreator() && (
                  <button
                    onClick={() => navigate('/create-challenge', { state: { clubId } })}
                    className="mt-4 sm:mt-6 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-lg font-semibold transition shadow-lg shadow-indigo-500/20 text-sm sm:text-base"
                  >
                    Créer le premier challenge
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {activeChallenges.map((challenge) => {
                  const status = getChallengeStatus(challenge);
                  const isParticipant = challenge.participants.some(p => p.user._id === user?._id);

                  return (
                    <div
                      key={challenge._id}
                      onClick={() => navigate(`/challenge/${challenge._id}`)}
                      className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg overflow-hidden hover:border-indigo-500 transition cursor-pointer group"
                    >
                      <div className="p-4 sm:p-6">
                        <div className="flex items-start justify-between mb-3 sm:mb-4 gap-2">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base sm:text-xl font-bold text-white group-hover:text-indigo-400 transition truncate">
                              {challenge.nom}
                            </h3>
                            <p className="text-gray-400 text-xs sm:text-sm mt-1 line-clamp-2">{challenge.description}</p>
                          </div>
                          <span className={`px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-semibold whitespace-nowrap flex-shrink-0 ${status.color}`}>
                            {status.text}
                          </span>
                        </div>

                        <div className="space-y-2 sm:space-y-3">
                          <div className="flex items-center gap-2 text-gray-300">
                            <Target size={14} className="text-indigo-500 flex-shrink-0 sm:w-4 sm:h-4" />
                            <span className="text-xs sm:text-sm truncate">{getTypeLabel(challenge.type)}</span>
                          </div>

                          <div className="flex items-center gap-2 text-gray-300">
                            <Calendar size={14} className="text-indigo-500 flex-shrink-0 sm:w-4 sm:h-4" />
                            <span className="text-xs sm:text-sm truncate">
                              {formatDate(challenge.dateDebut)} - {formatDate(challenge.dateFin)}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 text-gray-300">
                            <Users size={14} className="text-indigo-500 flex-shrink-0 sm:w-4 sm:h-4" />
                            <span className="text-xs sm:text-sm">{challenge.participants.length} participant(s)</span>
                          </div>
                        </div>

                        {isParticipant && (
                          <div className="mt-3 sm:mt-4 px-2 sm:px-3 py-1.5 sm:py-2 bg-indigo-900/30 border border-indigo-500 rounded-lg">
                            <p className="text-indigo-400 text-xs sm:text-sm font-semibold">✓ Vous participez</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {activeTab === 'recurrent' && (
          <>
            {recurringChallenges.length === 0 ? (
              <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-8 sm:p-12 text-center">
                <Repeat size={40} className="mx-auto text-gray-600 mb-4 sm:w-12 sm:h-12" />
                <p className="text-gray-400 text-base sm:text-lg">Aucun challenge hebdomadaire configuré</p>
                <p className="text-gray-500 text-xs sm:text-sm mt-2">
                  Les challenges hebdomadaires se répètent automatiquement chaque semaine
                </p>
                {isCreator() && (
                  <button
                    onClick={() => navigate('/create-challenge', { state: { clubId } })}
                    className="mt-4 sm:mt-6 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-lg font-semibold transition shadow-lg shadow-indigo-500/20 text-sm sm:text-base"
                  >
                    Créer un challenge hebdomadaire
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {recurringChallenges.map((template) => {
                  const currentWeekChallenge = getCurrentWeekChallengeForTemplate(template._id);
                  const now = new Date();
                  const isActive = currentWeekChallenge && new Date(currentWeekChallenge.dateDebut) <= now && new Date(currentWeekChallenge.dateFin) >= now;
                  const isUpcoming = currentWeekChallenge && new Date(currentWeekChallenge.dateDebut) > now;

                  return (
                    <div
                      key={template._id}
                      onClick={() => {
                        if (currentWeekChallenge) {
                          navigate(`/challenge/${currentWeekChallenge._id}`);
                        } else {
                          alert('Aucun challenge actif cette semaine. Le prochain challenge sera créé lundi prochain à minuit.');
                        }
                      }}
                      className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-4 sm:p-6 hover:border-indigo-500 transition cursor-pointer group"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 sm:mb-4 gap-3">
                        <div className="flex items-start gap-3">
                          <div className="bg-indigo-900/30 border border-indigo-500 p-2 sm:p-3 rounded-xl flex-shrink-0">
                            <Repeat size={20} className="text-indigo-400 sm:w-6 sm:h-6" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base sm:text-xl font-bold text-white group-hover:text-indigo-400 transition">{template.nom}</h3>
                            <p className="text-gray-400 text-xs sm:text-sm mt-1 line-clamp-2">{template.description}</p>
                          </div>
                        </div>
                        <span className={`px-2 sm:px-3 py-1 ${
                          isActive
                            ? 'bg-green-900/20 border border-green-500 text-green-400'
                            : isUpcoming
                            ? 'bg-blue-900/20 border border-blue-500 text-blue-400'
                            : 'bg-yellow-900/20 border border-yellow-500 text-yellow-400'
                        } rounded-full text-[10px] sm:text-xs font-semibold self-start whitespace-nowrap`}>
                          {isActive ? 'Challenge actif' : isUpcoming ? 'À venir' : 'En attente'}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
                        {template.enabledTypes?.map((typeConfig) => (
                          <div
                            key={typeConfig.type}
                            className="p-2 sm:p-3 bg-slate-800 border border-slate-700 rounded-lg"
                          >
                            <p className="text-indigo-400 font-semibold text-xs sm:text-sm truncate">
                              {getTypeLabel(typeConfig.type)}
                            </p>
                          </div>
                        ))}
                      </div>

                      <div className="mt-3 sm:mt-4 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-gray-400 text-xs sm:text-sm">
                          <Calendar size={14} className="flex-shrink-0 sm:w-4 sm:h-4" />
                          <span className="truncate">Se répète chaque lundi à minuit</span>
                        </div>
                        {currentWeekChallenge && (
                          <span className="text-indigo-400 text-xs font-semibold flex items-center gap-1">
                            Voir le challenge <Target size={12} />
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ✨ LEADERBOARD DU CLUB */}
        <div className="mt-6 sm:mt-8 bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-4 sm:p-6">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2 justify-center">
            <Medal size={20} className="text-yellow-500 sm:w-6 sm:h-6" />
            Classement du Club
          </h2>

          {leaderboard.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <Trophy size={40} className="mx-auto text-slate-700 mb-4 sm:w-12 sm:h-12" />
              <p className="text-sm sm:text-base text-gray-400">Aucun challenge terminé pour le moment</p>
              <p className="text-xs sm:text-sm text-gray-500 mt-2">Le classement apparaîtra une fois les premiers challenges complétés</p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4 max-w-4xl mx-auto">
              {leaderboard.map((participant, index) => {
                const isFirst = index === 0;
                const isCurrentUser = participant.user._id === user?._id;
                const medal = getMedalIcon(index);

                return (
                  <div
                    key={participant.user._id}
                    className={`group relative overflow-hidden rounded-xl transition-all duration-300 ${
                      isFirst
                        ? 'bg-gradient-to-br from-yellow-900/30 via-yellow-800/20 to-amber-900/30 border-2 border-yellow-500/50 shadow-xl shadow-yellow-500/20 hover:shadow-yellow-500/40 hover:scale-[1.02]'
                        : isCurrentUser
                        ? 'bg-gradient-to-br from-indigo-900/30 via-indigo-800/20 to-indigo-900/30 border-2 border-indigo-500/50 hover:border-indigo-400 hover:scale-[1.01]'
                        : 'bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 border-2 border-slate-700/50 hover:border-slate-600 hover:scale-[1.01]'
                    }`}
                  >
                    {/* Effet de brillance pour le premier */}
                    {isFirst && (
                      <>
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-500/10 to-transparent animate-pulse"></div>
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent"></div>
                        <div className="absolute -top-20 -right-20 w-40 h-40 bg-yellow-500/10 rounded-full blur-3xl animate-pulse"></div>
                      </>
                    )}

                    {/* Barre supérieure pour utilisateur actuel */}
                    {isCurrentUser && !isFirst && (
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-indigo-500 to-transparent"></div>
                    )}

                    <div className="relative z-10 p-3 sm:p-5">
                      {/* Layout */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">

                        {/* Première ligne: Position + Photo + Nom + Points (mobile) */}
                        <div className="flex items-center gap-3 sm:gap-5 flex-1 min-w-0">
                          {/* Position */}
                          <div className={`flex items-center justify-center w-10 sm:w-14 h-10 sm:h-14 flex-shrink-0 ${
                            isFirst
                              ? 'text-3xl sm:text-5xl animate-bounce'
                              : 'text-xl sm:text-3xl font-black text-gray-400'
                          }`}>
                            {medal || index + 1}
                          </div>

                          {/* Photo de profil */}
                          <div className="relative flex-shrink-0 group/avatar">
                            <div className={`absolute inset-0 rounded-full blur-md transition-opacity ${
                              isFirst
                                ? 'bg-yellow-500/30 animate-pulse'
                                : isCurrentUser
                                ? 'bg-indigo-500/30 opacity-0 group-hover/avatar:opacity-100'
                                : 'bg-slate-500/20 opacity-0 group-hover/avatar:opacity-100'
                            }`}></div>

                            <img
                              src={participant.user.photoProfil || defaultAvatar}
                              alt={`${participant.user.prenom} ${participant.user.nom}`}
                              className={`relative w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 transition-all duration-300 ${
                                isFirst
                                  ? 'border-yellow-500 ring-4 ring-yellow-500/20 group-hover:ring-yellow-500/40'
                                  : isCurrentUser
                                  ? 'border-indigo-500 group-hover:ring-2 group-hover:ring-indigo-500/50'
                                  : 'border-slate-600 group-hover:border-slate-500'
                              }`}
                            />

                            {/* Badge "Vous" */}
                            {isCurrentUser && (
                              <div className="absolute -bottom-1 -right-1 px-1.5 py-0.5 bg-indigo-600 text-white text-[9px] sm:text-[10px] font-bold rounded-full border-2 border-slate-900">
                                VOUS
                              </div>
                            )}
                          </div>

                          {/* Nom + Points (mobile) */}
                          <div className="flex-1 min-w-0 flex items-center justify-between sm:block">
                            <p className={`font-bold truncate ${
                              isFirst
                                ? 'text-sm sm:text-lg text-yellow-100'
                                : 'text-sm sm:text-base text-white'
                            }`}>
                              {participant.user.prenom} {participant.user.nom}
                            </p>

                            {/* Points (visible seulement mobile) */}
                            <div className="sm:hidden text-right">
                              <p className={`font-black ${
                                isFirst
                                  ? 'text-xl bg-gradient-to-b from-yellow-200 to-yellow-500 bg-clip-text text-transparent'
                                  : 'text-lg text-white'
                              }`}>
                                {participant.totalPoints} <span className="text-sm">pts</span>
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Points (visible seulement desktop) */}
                        <div className="hidden sm:block text-right flex-shrink-0">
                          <p className={`font-black ${
                            isFirst
                              ? 'text-2xl sm:text-3xl bg-gradient-to-b from-yellow-200 to-yellow-500 bg-clip-text text-transparent'
                              : 'text-xl sm:text-2xl text-white'
                          }`}>
                            {participant.totalPoints} <span className="text-base sm:text-lg">points</span>
                          </p>
                        </div>
                      </div>

                      {/* Challenges complétés */}
                      <div className="flex items-center gap-2 mt-2 ml-[52px] sm:ml-[88px]">
                        <div className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-gray-400">
                          <Trophy size={12} className="sm:w-3.5 sm:h-3.5" />
                          <span>{participant.challengesCompleted} challenge{participant.challengesCompleted > 1 ? 's' : ''} terminé{participant.challengesCompleted > 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    </div>

                    {/* Barre inférieure décorative */}
                    {isFirst && (
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent"></div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ✨ MODAL HISTORIQUE */}
        {showHistoryModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] sm:max-h-[85vh] overflow-hidden">
              {/* Header du modal */}
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-800">
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <div className="bg-slate-800 border border-slate-700 p-2 sm:p-3 rounded-xl flex-shrink-0">
                    <History size={20} className="text-indigo-400 sm:w-6 sm:h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg sm:text-2xl font-bold text-white truncate">Historique des Challenges</h2>
                    <p className="text-gray-400 text-xs sm:text-sm">{completedChallenges.length} challenge(s) terminé(s)</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="p-2 hover:bg-slate-800 rounded-lg transition flex-shrink-0 ml-2"
                >
                  <X size={20} className="text-gray-400 hover:text-white sm:w-6 sm:h-6" />
                </button>
              </div>

              {/* Contenu du modal */}
              <div className="p-3 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)] sm:max-h-[calc(85vh-100px)]">
                {completedChallenges.length === 0 ? (
                  <div className="text-center py-8 sm:py-12">
                    <Trophy size={40} className="mx-auto text-gray-600 mb-4 sm:w-12 sm:h-12" />
                    <p className="text-gray-400 text-base sm:text-lg">Aucun challenge terminé pour le moment</p>
                    <p className="text-gray-500 text-xs sm:text-sm mt-2">
                      Les challenges terminés apparaîtront ici
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    {completedChallenges.map((challenge) => {
                      const status = getChallengeStatus(challenge);
                      const isParticipant = challenge.participants.some(p => p.user._id === user?._id);

                      return (
                        <div
                          key={challenge._id}
                          onClick={() => {
                            setShowHistoryModal(false);
                            navigate(`/challenge/${challenge._id}`);
                          }}
                          className="bg-slate-800 border border-slate-700 rounded-xl shadow-lg overflow-hidden hover:border-indigo-500 transition cursor-pointer group"
                        >
                          <div className="p-3 sm:p-5">
                            <div className="flex items-start justify-between mb-2 sm:mb-3 gap-2">
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm sm:text-lg font-bold text-white group-hover:text-indigo-400 transition truncate">
                                  {challenge.nom}
                                </h3>
                                <p className="text-gray-400 text-xs sm:text-sm mt-1 line-clamp-2">{challenge.description}</p>
                              </div>
                              <span className={`px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-semibold whitespace-nowrap flex-shrink-0 ${status.color}`}>
                                {status.text}
                              </span>
                            </div>

                            <div className="space-y-1.5 sm:space-y-2">
                              <div className="flex items-center gap-2 text-gray-300">
                                <Target size={12} className="text-indigo-500 flex-shrink-0 sm:w-3.5 sm:h-3.5" />
                                <span className="text-[10px] sm:text-xs truncate">{getTypeLabel(challenge.type)}</span>
                              </div>

                              <div className="flex items-center gap-2 text-gray-300">
                                <Calendar size={12} className="text-indigo-500 flex-shrink-0 sm:w-3.5 sm:h-3.5" />
                                <span className="text-[10px] sm:text-xs truncate">
                                  {formatDate(challenge.dateDebut)} - {formatDate(challenge.dateFin)}
                                </span>
                              </div>

                              <div className="flex items-center gap-2 text-gray-300">
                                <Users size={12} className="text-indigo-500 flex-shrink-0 sm:w-3.5 sm:h-3.5" />
                                <span className="text-[10px] sm:text-xs">{challenge.participants.length} participant(s)</span>
                              </div>
                            </div>

                            {isParticipant && (
                              <div className="mt-2 sm:mt-3 px-2 sm:px-3 py-1.5 sm:py-2 bg-indigo-900/30 border border-indigo-500 rounded-lg">
                                <p className="text-indigo-400 text-[10px] sm:text-xs font-semibold">✓ Vous avez participé</p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default ClubChallenges;